import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Link,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  MenuItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Select,
  OutlinedInput,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import CloseIcon from '@mui/icons-material/Close';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import TimezoneUtils from '../utils/timezoneUtils';
import { EXTERNAL_LINK_REL, stripTrackingParams } from '../utils/externalUrlUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GridViewIcon from '@mui/icons-material/GridView';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import 'react-toastify/dist/ReactToastify.css';
import Dashboard from './Dashboard';
import LinkDetailsDialog from '../components/LinkDetailsDialog';
import InterviewCallsTab from '../components/InterviewCallsTab';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { Scatter, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
  ScatterController,
  BarElement,
  BarController,
  Tooltip as ChartTooltip
} from 'chart.js';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ScatterController,
  BarElement,
  BarController,
  Title,
  ChartTooltip,
  Legend
);

const MANUAL_CUSTOMIZED_URL_KEY = 'manual://customized';

const getBidDisplayUrl = (bid) => {
  if (!bid) return '';
  if (
    (bid.url === MANUAL_CUSTOMIZED_URL_KEY || bid.url === 'manual-application') &&
    bid.applicationEmail
  ) {
    return bid.applicationEmail;
  }
  return bid.url || '';
};

const getBidLinkHref = (bid) => {
  const displayUrl = getBidDisplayUrl(bid);
  if (displayUrl.includes('@')) {
    return `mailto:${displayUrl}`;
  }
  return stripTrackingParams(bid?.url || displayUrl);
};

const ALL_TEAM_MEMBERS = 'all';

const sortUsersWithBiddersFirst = (a, b) => {
  if (a.role === 'bidder' && b.role !== 'bidder') return -1;
  if (a.role !== 'bidder' && b.role === 'bidder') return 1;

  const roleOrder = { lead: 0, member: 1, bidder: 2 };
  const aOrder = roleOrder[a.role] ?? 3;
  const bOrder = roleOrder[b.role] ?? 3;

  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }

  return (a.name || a.email || '').localeCompare(b.name || b.email || '');
};

const getMasterId = (user) => {
  const master = user?.master;
  if (!master) return null;
  return master._id || master;
};

const getDirectBidders = (teamData, masterUserId) =>
  teamData
    .filter((user) => {
      if (user.role !== 'bidder') return false;
      const masterId = getMasterId(user);
      return masterId && masterId.toString() === masterUserId.toString();
    })
    .sort(sortUsersWithBiddersFirst);

const truncateDisplayUrl = (bid, maxLength) => {
  const displayUrl = getBidDisplayUrl(bid);
  return displayUrl.length > maxLength ? `${displayUrl.substring(0, maxLength)}...` : displayUrl;
};

const isManualEmailBid = (bid) =>
  bid?.url === MANUAL_CUSTOMIZED_URL_KEY || bid?.url === 'manual-application';

const BidHistory = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [bidHistory, setBidHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidHistoryLoading, setBidHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(() => {
    return TimezoneUtils.getTodayForInput();
  });
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCallUser, setSelectedCallUser] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [callUsersList, setCallUsersList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [chartUsersList, setChartUsersList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [openDashboard, setOpenDashboard] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [selectedBidIndex, setSelectedBidIndex] = useState(null);
  const [currentUserId, setCurrentUserId] = useState('');
  const [gridViewOpen, setGridViewOpen] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Add these props to pass to Dashboard
  const [dashboardProps, setDashboardProps] = useState(null);

  // Add new state variables for the call chart dialog
  const [callChartOpen, setCallChartOpen] = useState(false);
  const [selectedChartUser, setSelectedChartUser] = useState('');
  const [selectedChartResume, setSelectedChartResume] = useState('');
  const [userResumes, setUserResumes] = useState([]);
  const [interviewData, setInterviewData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [callChartDateRange, setCallChartDateRange] = useState('14');
  const [callChartCategoryFilter, setCallChartCategoryFilter] = useState([]);
  
  // Add state for link details dialog
  const [selectedLink, setSelectedLink] = useState(null);
  const [linkDetailsDialogOpen, setLinkDetailsDialogOpen] = useState(false);
  const [linkDetailsLoading, setLinkDetailsLoading] = useState(false);
  const [teamTechStacks, setTeamTechStacks] = useState([]);
  
  // Add state for chart date details dialog
  const [chartDateDetailsOpen, setChartDateDetailsOpen] = useState(false);
  const [selectedDateEmails, setSelectedDateEmails] = useState([]);
  

  // Add a function to get user name by userId - moved here to avoid TDZ issues
  const formatMemberDisplayName = (member) => {
    if (!member) return 'Unknown User';
    let displayName = member.name || member.email || 'Unknown User';
    if (member.role === 'lead') {
      displayName = `${displayName} (Lead)`;
    } else if (member.role === 'bidder') {
      displayName = `${displayName} (Bidder)`;
    } else if (member.role === 'member') {
      displayName = `${displayName} (Member)`;
    }
    return displayName;
  };

  const getUserNameById = (userId) => {
    if (!userId || userId === ALL_TEAM_MEMBERS) {
      return userId === ALL_TEAM_MEMBERS ? 'All Members' : 'Unknown User';
    }

    const member = teamMembers.find((teamMember) => String(teamMember._id) === String(userId));
    if (member) {
      return formatMemberDisplayName(member);
    }

    if (String(userId) === String(currentUserId) && user) {
      return user.name || user.email || 'Me';
    }

    const listed = usersList.find((entry) => String(entry._id) === String(userId))
      || callUsersList.find((entry) => String(entry._id) === String(userId));
    if (listed && listed.name && listed.name !== 'My Applications' && listed.name !== 'All Members') {
      return listed.name;
    }

    return 'Unknown User';
  };

  const getBidTabUserIds = () => {
    if (selectedUser === ALL_TEAM_MEMBERS) {
      return teamMembers.map((member) => member._id);
    }
    return selectedUser ? [selectedUser] : [];
  };

  const getCallTabUserIds = () => {
    if (selectedCallUser === ALL_TEAM_MEMBERS) {
      return teamMembers
        .filter((member) => member.role !== 'bidder')
        .map((member) => member._id);
    }
    return selectedCallUser ? [selectedCallUser] : [];
  };


  // Helper function to format resume name with nickname (real name in parenthesis)
  const formatResumeName = (resume) => {
    const nickname = resume?.nickname;
    const realName = resume?.content?.personal_info?.name;
    
    if (nickname && realName) {
      return `${nickname} (${realName})`;
    } else if (nickname) {
      return nickname;
    } else if (realName) {
      return realName;
    }
    return 'Untitled Resume';
  };

  // Helper function to format queryDateLimit for LinkDetailsDialog
  const formatQueryDateLimit = (days) => {
    if (days === null || days === undefined) return "Any time";
    switch (days) {
      case 1:
        return "Past 24 hours";
      case 7:
        return "Past week";
      case 30:
        return "Past month";
      case 365:
        return "Past year";
      default:
        return `${days} days`;
    }
  };

  // Add getRelativeTimeString function from BidLinks
  const getRelativeTimeString = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle days + hours specially
    if (diffInSeconds >= 86400) {
      // More than a day
      const days = Math.floor(diffInSeconds / 86400);
      const remainingHours = Math.floor((diffInSeconds % 86400) / 3600);

      if (remainingHours === 0) {
        return days === 1 ? "1 day ago" : `${days} days ago`;
      }
      return days === 1
        ? `1 day ${remainingHours} ${
            remainingHours === 1 ? "hour" : "hours"
          } ago`
        : `${days} days ${remainingHours} ${
            remainingHours === 1 ? "hour" : "hours"
          } ago`;
    }
    const intervals = {
      hour: 3600,
      minute: 60,
    };

    for (const [unit, seconds] of Object.entries(intervals)) {
      const interval = Math.floor(diffInSeconds / seconds);
      if (interval >= 1) {
        return interval === 1 ? `1 ${unit} ago` : `${interval} ${unit}s ago`;
      }
    }

    return "just now";
  };

  // Get unique profiles from all available data (not just filtered)
  const getUniqueProfiles = () => {
    // Get profiles from all possible data sources
    let allBids = [...bidHistory];
    if (globalSearchResults.length > 0) {
      allBids = [...allBids, ...globalSearchResults];
    }
    if (globalUserSearchResults.length > 0) {
      allBids = [...allBids, ...globalUserSearchResults];
    }
    
    const profiles = allBids
      .map(bid => bid.profile)
      .filter(profile => profile && profile.trim() !== '')
      .filter((profile, index, self) => self.indexOf(profile) === index)
      .sort();
    return profiles;
  };

  // Get unique users from all available data (not just filtered)
  const getUniqueUsers = () => {
    // Get users from all possible data sources
    let allBids = [...bidHistory];
    if (globalSearchResults.length > 0) {
      allBids = [...allBids, ...globalSearchResults];
    }
    if (globalUserSearchResults.length > 0) {
      allBids = [...allBids, ...globalUserSearchResults];
    }
    
    const users = allBids
      .map(bid => bid.userName || getUserNameById(bid.userId))
      .filter(user => user && user.trim() !== '')
      .filter((user, index, self) => self.indexOf(user) === index)
      .sort();
    return users;
  };

  // Filter interview data based on selected date range (similar to bid chart dashboard)
  const filteredInterviewData = useMemo(() => {
    const rangeDays = parseInt(callChartDateRange, 10);
    if (!Number.isFinite(rangeDays)) return interviewData;
    const now = TimezoneUtils.now();
    const start = TimezoneUtils.addDaysUTC(now, -rangeDays);
    const startOfDay = TimezoneUtils.startOfDayUTC(start);
    const endOfDay = TimezoneUtils.endOfDayUTC(now);
    
    // Generate complete date range to fill in missing dates
    const allDates = TimezoneUtils.generateDateRange(startOfDay, TimezoneUtils.startOfDayUTC(now));
    
    // Create a map of existing data for quick lookup
    const dataMap = {};
    (interviewData || []).forEach(item => {
      const itemDate = new Date(item.date);
      if (itemDate >= startOfDay && itemDate <= endOfDay) {
        dataMap[item.date] = item;
      }
    });
    
    // Fill in all dates (including missing ones with count 0)
    let filled = allDates.map(date => {
      if (dataMap[date]) {
        return dataMap[date];
      } else {
        return {
          date: date,
          count: 0,
          emails: []
        };
      }
    });
    
    // Filter by category if any categories are selected
    if (callChartCategoryFilter.length > 0) {
      filled = filled.map(item => {
        // Filter emails within each date by selected categories
        const filteredEmails = (item.emails || []).filter(email => 
          callChartCategoryFilter.includes(email.category)
        );
        
        return {
          ...item,
          emails: filteredEmails,
          count: filteredEmails.length
        };
      });
      // Keep all dates, even with count 0 after filtering
    }
    
    return filled;
  }, [interviewData, callChartDateRange, callChartCategoryFilter]);

  // Add global user search state variables
  const [globalUserSearchResults, setGlobalUserSearchResults] = useState([]);
  const [isGlobalUserSearching, setIsGlobalUserSearching] = useState(false);
  const [showGlobalUserResults, setShowGlobalUserResults] = useState(false);

  // Add state for tabs
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    if (location.state?.openMailsTab || location.state?.openCallTab) {
      setCurrentTab(1);
      window.history.replaceState({}, document.title);
    } else if (location.state?.openCallsTab) {
      setCurrentTab(2);
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.openMailsTab, location.state?.openCallTab, location.state?.openCallsTab]);

  // Add state for Call tab (interview invitations)
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [emailTitleFilter, setEmailTitleFilter] = useState('');
  const [emailProfileFilter, setEmailProfileFilter] = useState('');
  const [emailUserFilter, setEmailUserFilter] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [globalEmailSearchResults, setGlobalEmailSearchResults] = useState([]);
  const [isEmailSearching, setIsEmailSearching] = useState(false);
  const [globalUserEmailSearchResults, setGlobalUserEmailSearchResults] = useState([]);
  const [isGlobalUserEmailSearching, setIsGlobalUserEmailSearching] = useState(false);
  const [showGlobalUserEmailResults, setShowGlobalUserEmailResults] = useState(false);
  const [emailBodyLoading, setEmailBodyLoading] = useState(false);
  const [fullInterviewChartData, setFullInterviewChartData] = useState([]);

  // Email category options (matching backend categories)
  const emailCategories = [
    'Interview_Invitation',
    'Interview_Booking',
    'Interview/AI-Interview',
    'Technical_Assignment',
    'Action_Required',
    'Necessary'
  ];

  // Helper function to format category names for display
  const formatCategoryName = (category) => {
    if (!category) return '';
    return category.replace(/_/g, ' ').replace(/\//g, ' / ');
  };

  // Step 1: Load user role and team members first
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        
        // Fetch user data and team members in parallel
        const [userResponse, teamResponse, teamInfoResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/auth/user`),
          axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`),
          axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team-info`).catch(() => ({ data: {} })),
        ]);
        setTeamTechStacks(teamInfoResponse.data?.team?.techStacks || []);

        const userData = userResponse.data;
        const teamData = Array.isArray(teamResponse.data) ? teamResponse.data : [];

        setUserRole(userData.role);
        setCurrentUserId(userData._id);

        // Helper function to format user display name
        const formatUserDisplayName = (user) => {
          let displayName = user.name || user.email;
          
          // Add role label
          if (user.role === 'bidder' && user.master && user.master.name) {
            // Show master's name for bidders
            displayName = `${displayName} (${user.master.name}'s Bidder)`;
          } else if (user.role === 'bidder') {
            displayName = `${displayName} (Bidder)`;
          } else if (user.role === 'lead') {
            displayName = `${displayName} (Lead)`;
          } else {
            displayName = `${displayName} (Member)`;
          }
          
          return {
            _id: user._id,
            name: displayName,
            role: user.role,
          };
        };

        // Set up chart users list - always show all team members for the chart
        const allTeamUsersForChart = teamData
          .filter(user => user._id !== userData._id)
          .sort(sortUsersWithBiddersFirst)
          .map(formatUserDisplayName);

        setChartUsersList([
          { _id: userData._id, name: 'My Applications' },
          ...allTeamUsersForChart
        ]);

        // Set up users list for bid history - filter based on role
        const role = userData.role;
        const isLeadLike = role === 'lead' || role === 'boss';
        const directBidders = getDirectBidders(teamData, userData._id)
          .map(formatUserDisplayName);

        let filteredTeamUsers;
        if (isLeadLike) {
          // Leads/bosses can select any team member (including bidders)
          filteredTeamUsers = teamData
            .filter(user => user._id !== userData._id)
            .sort(sortUsersWithBiddersFirst)
            .map(formatUserDisplayName);
        } else if (role === 'member' || role === 'restricted developer' || directBidders.length > 0) {
          // Members (and anyone with bidders) see themselves and their direct bidders
          filteredTeamUsers = directBidders;
        } else {
          // Bidders only see themselves
          filteredTeamUsers = [];
        }

        if (isLeadLike) {
          setUsersList([
            { _id: ALL_TEAM_MEMBERS, name: 'All Members' },
            { _id: userData._id, name: 'My Applications' },
            ...filteredTeamUsers
          ]);
        } else {
          setUsersList([
            { _id: userData._id, name: 'My Applications' },
            ...filteredTeamUsers
          ]);
        }
        setSelectedUser(userData._id);

        setTeamMembers(teamData);

        // Mails tab: members can select me + my bidders; leads can view all non-bidder members
        const nonBidderTeamUsers = teamData
          .filter((user) => user._id !== userData._id && user.role !== 'bidder')
          .sort(sortUsersWithBiddersFirst)
          .map(formatUserDisplayName);

        if (isLeadLike) {
          setCallUsersList([
            { _id: ALL_TEAM_MEMBERS, name: 'All Members' },
            { _id: userData._id, name: 'My Applications' },
            ...nonBidderTeamUsers,
          ]);
          setSelectedCallUser(ALL_TEAM_MEMBERS);
        } else if (role === 'member' || role === 'restricted developer' || directBidders.length > 0) {
          setCallUsersList([
            { _id: userData._id, name: 'My Applications' },
            ...directBidders,
          ]);
          setSelectedCallUser(userData._id);
        } else {
          setCallUsersList([
            { _id: userData._id, name: 'My Applications' },
          ]);
          setSelectedCallUser(userData._id);
        }

        // Prepare dashboard props to avoid double loading
        setDashboardProps({
          userRole: userData.role,
          currentUserId: userData._id,
          teamMembers: teamData,
          preloaded: true
        });

        setUserDataLoaded(true);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load user data:', err);
        toast.error('Failed to load user data');
        setError('Failed to load user data');
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  // Step 2: Load bid history after user data is loaded
  useEffect(() => {
    if (!userDataLoaded || !selectedUser) return;

    const fetchBidHistory = async () => {
      setBidHistoryLoading(true);
      try {
        const fromDate = new Date(dateFilter);
        const fromGMT = TimezoneUtils.startOfDayUTC(fromDate);
        const toGMT = TimezoneUtils.endOfDayUTC(fromDate);
        const dateParams = {
          fromDate: fromGMT.toISOString(),
          toDate: toGMT.toISOString(),
        };
        const userIds = getBidTabUserIds();

        const responses = await Promise.all(
          userIds.map((userId) =>
            axios
              .get(`${process.env.REACT_APP_API_URL}/applications`, {
                params: { ...dateParams, userId },
              })
              .catch(() => ({ data: [] }))
          )
        );

        const allBids = responses
          .flatMap((response, index) =>
            (response.data || []).map((bid) => ({
              ...bid,
              userName: bid.userName || getUserNameById(userIds[index]),
            }))
          )
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setBidHistory(allBids);
        setBidHistoryLoading(false);
      } catch (err) {
        console.error('Failed to fetch bid history:', err);
        setError('Failed to fetch bid history');
        setBidHistoryLoading(false);
      }
    };

    fetchBidHistory();
  }, [dateFilter, selectedUser, userDataLoaded, teamMembers]);

  // Step 3: Load email history for Call tab
  useEffect(() => {
    if (!userDataLoaded || currentTab !== 1 || !selectedCallUser) return;

    const fetchEmailHistory = async () => {
      setEmailHistoryLoading(true);
      try {
        const fromDate = new Date(dateFilter);
        const fromGMT = TimezoneUtils.startOfDayUTC(fromDate);
        const toGMT = TimezoneUtils.endOfDayUTC(fromDate);
        const userIds = getCallTabUserIds();

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/applications/emails/by-users`,
          {
            params: {
              userIds: userIds.join(','),
              fromDate: fromGMT.toISOString(),
              toDate: toGMT.toISOString(),
            },
          }
        );

        setEmailHistory(response.data || []);
        setEmailHistoryLoading(false);
      } catch (err) {
        console.error('Failed to fetch email history:', err);
        toast.error('Failed to fetch email history');
        setEmailHistoryLoading(false);
      }
    };

    fetchEmailHistory();
  }, [dateFilter, selectedCallUser, userDataLoaded, currentTab, teamMembers]);

  const handleCallUserChange = (newUserId) => {
    setSelectedCallUser(newUserId);
    setEmailHistory([]);
    setGlobalEmailSearchResults([]);
    setGlobalUserEmailSearchResults([]);
    setShowGlobalUserEmailResults(false);
    setEmailProfileFilter('');
    setEmailUserFilter('');
  };

  // Handle user selection change - clear bid history immediately
  const handleUserChange = (newUserId) => {
    setSelectedUser(newUserId);
    setBidHistory([]); // Clear the bid history immediately
    setGlobalSearchResults([]); // Also clear any search results
    setProfileFilter(''); // Clear profile filter
    setUserFilter(''); // Clear user filter
    setEmailHistory([]); // Clear email history
    setGlobalEmailSearchResults([]);
    setGlobalUserEmailSearchResults([]);
    setShowGlobalUserEmailResults(false);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Clear search results when switching tabs
    if (newValue === 0) {
      setGlobalEmailSearchResults([]);
      setGlobalUserEmailSearchResults([]);
      setShowGlobalUserEmailResults(false);
      setEmailTitleFilter('');
    } else {
      setGlobalSearchResults([]);
      setGlobalUserSearchResults([]);
      setShowGlobalUserResults(false);
      setTitleFilter('');
    }
  };

  const filteredBids = useMemo(() => bidHistory.filter(bid => {
    const matchesTitle = getBidDisplayUrl(bid).toLowerCase().includes(titleFilter.toLowerCase());
    
    let matchesEmailContent = true;
    if (titleFilter && bid.mailedResumeId) {
      const coverLetterTitle = bid.mailedResumeId?.cover_letter?.title || '';
      const applicationEmail = bid.applicationEmail || '';
      const searchLower = titleFilter.toLowerCase();
      matchesEmailContent = 
        coverLetterTitle.toLowerCase().includes(searchLower) ||
        applicationEmail.toLowerCase().includes(searchLower);
    }
    
    const matchesProfile = !profileFilter || (bid.profile && bid.profile.toLowerCase().includes(profileFilter.toLowerCase()));
    const matchesUser = !userFilter || (bid.userName && bid.userName.toLowerCase().includes(userFilter.toLowerCase())) || 
                       (bid.userEmail && bid.userEmail.toLowerCase().includes(userFilter.toLowerCase())) ||
                       (getUserNameById(bid.userId) && getUserNameById(bid.userId).toLowerCase().includes(userFilter.toLowerCase()));
    return (matchesTitle || matchesEmailContent) && matchesProfile && matchesUser;
  }), [bidHistory, titleFilter, profileFilter, userFilter, usersList, callUsersList, teamMembers]);

  const handleOpenDetails = (bid) => {
    const index = filteredBids.findIndex(item => item._id === bid._id);
    setSelectedBid(bid);
    setSelectedBidIndex(index);
    setDialogOpen(true);
  };

  const handleOpenLinkDetails = async (bid) => {
    setLinkDetailsLoading(true);
    setLinkDetailsDialogOpen(true);
    
    try {
      // Fetch link details from backend using the URL
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/bid-links/url`, {
        params: {
          url: bid.url,
          showBlacklisted: false
        }
      });
      
      if (response.data) {
        setSelectedLink(response.data);
      } else {
        // If no link found in bid_links, use the bid data as fallback
        setSelectedLink(bid);
        toast.info('Link details not found in bid links database');
      }
    } catch (error) {
      console.error('Failed to fetch link details:', error);
      // Use the bid data as fallback
      setSelectedLink(bid);
      toast.error('Failed to fetch link details from database');
    } finally {
      setLinkDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDialogOpen(false);
    setSelectedBid(null);
    setSelectedBidIndex(null);
    setImageLoading(true);
  };

  const handlePreviousBid = () => {
    if (selectedBidIndex > 0) {
      const newIndex = selectedBidIndex - 1;
      setSelectedBidIndex(newIndex);
      setSelectedBid(filteredBids[newIndex]);
      setImageLoading(true);
    }
  };

  const handleNextBid = () => {
    if (selectedBidIndex < filteredBids.length - 1) {
      const newIndex = selectedBidIndex + 1;
      setSelectedBidIndex(newIndex);
      setSelectedBid(filteredBids[newIndex]);
      setImageLoading(true);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!dialogOpen) return;
      
      if (e.key === 'ArrowLeft') {
        handlePreviousBid();
      } else if (e.key === 'ArrowRight') {
        handleNextBid();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialogOpen, selectedBidIndex, filteredBids]);

  const handleGlobalSearch = async () => {
    if (!titleFilter.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsSearching(true);
    try {
      const searchUserId = selectedUser === ALL_TEAM_MEMBERS ? currentUserId : selectedUser;
      const endpoint = selectedUser === ALL_TEAM_MEMBERS
        ? 'team-search'
        : 'global-user-search';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/applications/${endpoint}`, {
          params: { 
            search: titleFilter,
            userId: searchUserId 
          }
        }
      );
      setGlobalSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to perform global search:', error);
      toast.error('Failed to perform global search');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setGlobalSearchResults([]);
    setTitleFilter('');
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (currentTab === 0) {
        // Bid tab
        if (e.ctrlKey && e.key === 'Enter' && titleFilter.trim()) {
          e.preventDefault();
          handleGlobalUserSearch();
        } else if (e.key === 'Enter' && titleFilter.trim()) {
          e.preventDefault();
          handleGlobalSearch();
        }
      } else if (currentTab === 1) {
        // Call tab
        if (e.ctrlKey && e.key === 'Enter' && emailTitleFilter.trim()) {
          e.preventDefault();
          handleEmailGlobalUserSearch();
        } else if (e.key === 'Enter' && emailTitleFilter.trim()) {
          e.preventDefault();
          handleEmailGlobalSearch();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [titleFilter, emailTitleFilter, currentTab]);

  const handleGlobalUserSearch = async () => {
    if (!titleFilter.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsGlobalUserSearching(true);
    try {
      // Use the team-search endpoint to search all team members
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/applications/team-search`, {
          params: { 
            search: titleFilter,
            userId: currentUserId  // Pass current user ID to identify team
          }
        }
      );
      
      // Map the response data to include user names (from backend now)
      const resultsWithUserNames = Array.isArray(response.data) 
        ? response.data.map(item => ({
            ...item,
            userName: item.userName || getUserNameById(item.userId)
          }))
        : [];
      
      setGlobalUserSearchResults(resultsWithUserNames);
      setShowGlobalUserResults(true);
    } catch (error) {
      console.error('Failed to perform global user search:', error);
      toast.error('Failed to perform team-wide search');
    } finally {
      setIsGlobalUserSearching(false);
    }
  };

  const handleClearGlobalUserSearch = () => {
    setGlobalUserSearchResults([]);
    setShowGlobalUserResults(false);
    setTitleFilter('');
  };

  const handleClearFilters = () => {
    setProfileFilter('');
    setUserFilter('');
  };

  // Email filtering and search handlers
  const handleEmailGlobalSearch = async () => {
    if (!emailTitleFilter.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsEmailSearching(true);
    try {
      const userIds = getCallTabUserIds();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/applications/emails/search`,
        {
          params: {
            userIds: userIds.join(','),
            search: emailTitleFilter.trim(),
          },
        }
      );

      setGlobalEmailSearchResults(response.data || []);
    } catch (error) {
      console.error('Failed to perform email search:', error);
      toast.error('Failed to perform email search');
    } finally {
      setIsEmailSearching(false);
    }
  };

  const handleEmailGlobalUserSearch = async () => {
    if (!emailTitleFilter.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsGlobalUserEmailSearching(true);
    try {
      const nonBidderMembers = teamMembers.filter((member) => member.role !== 'bidder');
      const userIds = nonBidderMembers.map((member) => member._id);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/applications/emails/search`,
        {
          params: {
            userIds: userIds.join(','),
            search: emailTitleFilter.trim(),
          },
        }
      );

      setGlobalUserEmailSearchResults(response.data || []);
      setShowGlobalUserEmailResults(true);
    } catch (error) {
      console.error('Failed to perform global user email search:', error);
      toast.error('Failed to perform team-wide email search');
    } finally {
      setIsGlobalUserEmailSearching(false);
    }
  };

  const handleClearEmailSearch = () => {
    setGlobalEmailSearchResults([]);
    setEmailTitleFilter('');
  };

  const handleClearGlobalUserEmailSearch = () => {
    setGlobalUserEmailSearchResults([]);
    setShowGlobalUserEmailResults(false);
    setEmailTitleFilter('');
  };

  const handleOpenEmailDetails = async (email) => {
    setSelectedEmail(email);
    setEmailDialogOpen(true);

    if (email.body || !email._id) {
      return;
    }

    setEmailBodyLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/applications/emails/detail/${email._id}`
      );
      setSelectedEmail((prev) => (prev?._id === email._id ? { ...prev, body: response.data.body } : prev));
    } catch (error) {
      console.error('Failed to load email body:', error);
      toast.error('Failed to load email content');
    } finally {
      setEmailBodyLoading(false);
    }
  };

  const handleCloseEmailDetails = () => {
    setEmailDialogOpen(false);
    setSelectedEmail(null);
    setEmailBodyLoading(false);
  };

  const loadInterviewChartForUser = async (userId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/applications/interview-invitations/by-user/${userId}`
    );
    const resumes = (response.data.resumes || []).map((resume) => ({
      _id: resume._id,
      nickname: resume.nickname,
      content: { personal_info: { name: resume.name } },
    }));
    const chartData = response.data.data || [];
    return { resumes, chartData };
  };

  const filterInterviewChartByResume = (chartData, resumeId) =>
    chartData
      .map((day) => {
        const emails = day.emails.filter((email) => String(email.resumeId) === String(resumeId));
        return {
          ...day,
          emails,
          count: new Set(emails.map((email) => email.from)).size,
        };
      })
      .filter((day) => day.emails.length > 0);

  const getResumeFileUrl = (path) => {
    if (!path) return null;
    return `${process.env.REACT_APP_API_URL}/resumefiles/${path}`;
  };

  const handleOpenCustomizedResume = (bid) => {
    if (bid?.generatedResumePath) {
      window.open(getResumeFileUrl(bid.generatedResumePath), '_blank', 'noopener,noreferrer');
      return;
    }

    toast.error('Customized resume file not available');
  };

  const handleOpenResumeFile = async (email) => {
    if (email.resumePath) {
      window.open(getResumeFileUrl(email.resumePath), '_blank', 'noopener,noreferrer');
      return;
    }

    if (!email.resumeId) {
      toast.error('Resume not available');
      return;
    }

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/resumes/${email.resumeId}`);
      if (response.data?.path) {
        window.open(getResumeFileUrl(response.data.path), '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Resume file not found');
      }
    } catch (error) {
      console.error('Failed to open resume:', error);
      toast.error('Failed to open resume');
    }
  };

  // Get unique profiles from email data
  const getUniqueEmailProfiles = () => {
    let allEmails = [...emailHistory];
    if (globalEmailSearchResults.length > 0) {
      allEmails = [...allEmails, ...globalEmailSearchResults];
    }
    if (globalUserEmailSearchResults.length > 0) {
      allEmails = [...allEmails, ...globalUserEmailSearchResults];
    }
    
    const profiles = allEmails
      .map(email => email.resumeName)
      .filter(profile => profile && profile.trim() !== '')
      .filter((profile, index, self) => self.indexOf(profile) === index)
      .sort();
    return profiles;
  };

  // Get unique users from email data
  const getUniqueEmailUsers = () => {
    let allEmails = [...emailHistory];
    if (globalEmailSearchResults.length > 0) {
      allEmails = [...allEmails, ...globalEmailSearchResults];
    }
    if (globalUserEmailSearchResults.length > 0) {
      allEmails = [...allEmails, ...globalUserEmailSearchResults];
    }
    
    const users = allEmails
      .map(email => email.userName)
      .filter(user => user && user.trim() !== '')
      .filter((user, index, self) => self.indexOf(user) === index)
      .sort();
    return users;
  };

  // Filter emails based on all filters
  const filteredEmails = useMemo(() => emailHistory.filter(email => {
    const searchLower = emailTitleFilter?.toLowerCase();
    const matchesTitle = !emailTitleFilter || 
      email.subject?.toLowerCase().includes(searchLower) ||
      email.from?.toLowerCase().includes(searchLower) ||
      email.snippet?.toLowerCase().includes(searchLower);
    
    const matchesProfile = !emailProfileFilter || 
      (email.resumeName && email.resumeName.toLowerCase().includes(emailProfileFilter.toLowerCase()));
    
    const matchesUser = !emailUserFilter || 
      (email.userName && email.userName.toLowerCase().includes(emailUserFilter.toLowerCase()));
    
    const matchesCategory = categoryFilter.length === 0 || 
      categoryFilter.some(cat => email.category?.toLowerCase().includes(cat.toLowerCase()));
    
    return matchesTitle && matchesProfile && matchesUser && matchesCategory;
  }), [emailHistory, emailTitleFilter, emailProfileFilter, emailUserFilter, categoryFilter]);

  const displayedEmails = useMemo(() => {
    const emails = showGlobalUserEmailResults 
      ? globalUserEmailSearchResults 
      : (globalEmailSearchResults.length > 0 ? globalEmailSearchResults : filteredEmails);
    
    return emails.filter(email => {
      const matchesProfile = !emailProfileFilter || 
        (email.resumeName && email.resumeName.toLowerCase().includes(emailProfileFilter.toLowerCase()));
      
      const matchesUser = !emailUserFilter || 
        (email.userName && email.userName.toLowerCase().includes(emailUserFilter.toLowerCase()));
      
      const matchesCategory = categoryFilter.length === 0 || 
        categoryFilter.some(cat => email.category?.toLowerCase().includes(cat.toLowerCase()));
      
      return matchesProfile && matchesUser && matchesCategory;
    });
  }, [
    showGlobalUserEmailResults,
    globalUserEmailSearchResults,
    globalEmailSearchResults,
    filteredEmails,
    emailProfileFilter,
    emailUserFilter,
    categoryFilter,
  ]);

  const displayedBids = useMemo(() => {
    const bids = showGlobalUserResults 
      ? globalUserSearchResults 
      : (globalSearchResults.length > 0 ? globalSearchResults : filteredBids);
    
    return bids.filter(bid => {
      const matchesProfile = !profileFilter || (bid.profile && bid.profile.toLowerCase().includes(profileFilter.toLowerCase()));
      const matchesUser = !userFilter || (bid.userName && bid.userName.toLowerCase().includes(userFilter.toLowerCase())) || 
                         (bid.userEmail && bid.userEmail.toLowerCase().includes(userFilter.toLowerCase())) ||
                         (getUserNameById(bid.userId) && getUserNameById(bid.userId).toLowerCase().includes(userFilter.toLowerCase()));
      
      let matchesEmailContent = true;
      if (titleFilter && bid.mailedResumeId) {
        const coverLetterTitle = bid.mailedResumeId?.cover_letter?.title || '';
        const applicationEmail = bid.applicationEmail || '';
        const searchLower = titleFilter.toLowerCase();
        matchesEmailContent = 
          coverLetterTitle.toLowerCase().includes(searchLower) ||
          applicationEmail.toLowerCase().includes(searchLower) ||
          getBidDisplayUrl(bid).toLowerCase().includes(searchLower);
      }
      
      return matchesProfile && matchesUser && matchesEmailContent;
    });
  }, [
    showGlobalUserResults,
    globalUserSearchResults,
    globalSearchResults,
    filteredBids,
    profileFilter,
    userFilter,
    titleFilter,
    usersList,
    callUsersList,
    teamMembers,
  ]);

  const shouldShowEmailUserColumn = () => {
    if (selectedCallUser === ALL_TEAM_MEMBERS) return true;
    if (showGlobalUserEmailResults) return true;
    if (globalEmailSearchResults.length > 0) return true;
    return displayedEmails.some(email => email.userName);
  };

  // Add a function to check if we should show the user column
  const shouldShowUserColumn = () => {
    if (selectedUser === ALL_TEAM_MEMBERS) return true;

    // Show user column if we have global user search results
    if (showGlobalUserResults) return true;
    
    // Show user column if we have global search results (team-wide search)
    if (globalSearchResults.length > 0) return true;
    
    // Show user column if we have any bids with user information
    return displayedBids.some(bid => bid.userName || bid.userEmail || bid.userRole);
  };

  const getBidOwnerId = (bid) => {
    if (!bid?.userId) return '';
    if (typeof bid.userId === 'object' && bid.userId._id) {
      return String(bid.userId._id);
    }
    return String(bid.userId);
  };

  const canDeleteBid = (bid) => {
    const ownerId = getBidOwnerId(bid);
    return usersList.some((user) => String(user._id) === ownerId);
  };

  const handleDelete = async (bidId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/applications/${bidId}`);
      const removeBid = (bid) => bid._id !== bidId;
      setBidHistory((prevBids) => prevBids.filter(removeBid));
      setGlobalSearchResults((prev) => prev.filter(removeBid));
      setGlobalUserSearchResults((prev) => prev.filter(removeBid));
      toast.success('Application deleted successfully');
    } catch (error) {
      console.error('Failed to delete application:', error);
      const message = error.response?.data?.message || 'Failed to delete application';
      toast.error(message);
    }
  };

  const handleOpenDashboard = () => {
    setOpenDashboard(true);
  };

  const handleCloseDashboard = () => {
    setOpenDashboard(false);
  };

  const getTimeScatterData = (bids) => {
    // Filter bids for chart based on user permissions
    let chartBids = bids;
    if (userRole !== 'lead' && userRole !== 'boss') {
      const allowedUserIds = usersList.map(user => user._id);
      chartBids = bids.filter(bid => 
        allowedUserIds.includes(bid.userId) || allowedUserIds.includes(selectedUser)
      );
    }
    
    return {
      datasets: [{
        label: 'Bid Times',
        data: chartBids.map(bid => {
          const bidDate = new Date(bid.timestamp);
          return {
            x: bidDate.getUTCHours() + (bidDate.getUTCMinutes() / 60),
            y: 1
          };
        }),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        type: 'bar',
        barThickness: 2,
        barPercentage: 0.1,
      }]
    };
  };

  const timeScatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: 0,
        max: 24,
        title: {
          display: true,
          text: 'Hour of Day'
        },
        ticks: {
          callback: (value) => {
            return `${Math.floor(value)}:${String(Math.floor((value % 1) * 60)).padStart(2, '0')}`;
          }
        }
      },
      y: {
        min: 0,
        max: 1,
        display: false,
        grid: {
          display: false
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: 'Bid Time Distribution'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const hour = Math.floor(context.parsed.x);
            const minute = Math.floor((context.parsed.x % 1) * 60);
            return `Time: ${hour}:${String(minute).padStart(2, '0')}`;
          }
        }
      }
    }
  };

  const handleOpenGridView = () => {
    setGridViewOpen(true);
  };

  const handleCloseGridView = () => {
    setGridViewOpen(false);
  };

  const handleGridItemClick = (bid) => {
    setGridViewOpen(false);
    const index = filteredBids.findIndex(item => item._id === bid._id);
    setSelectedBid(bid);
    setSelectedBidIndex(index);
    setDialogOpen(true);
  };

  const handleCopyAllLinks = () => {
    if (displayedBids.length === 0) {
      toast.error('No links to copy');
      return;
    }
    const allLinks = displayedBids.map((bid) => getBidDisplayUrl(bid)).join('\n');
    navigator.clipboard.writeText(allLinks)
      .then(() => toast.success('All links copied!'))
      .catch(() => toast.error('Failed to copy links'));
  };

  // Add new functions for the call chart
  // ... existing code ...

// Add new functions for the call chart
const handleOpenCallChart = async () => {
  setCallChartOpen(true);
  const chartUserId = selectedCallUser === ALL_TEAM_MEMBERS ? currentUserId : selectedCallUser;
  setSelectedChartUser(chartUserId);
  setSelectedChartResume('all');
  setUserResumes([]);
  setInterviewData([]);
  setFullInterviewChartData([]);
  
  if (chartUserId) {
    try {
      setChartLoading(true);
      const { resumes, chartData } = await loadInterviewChartForUser(chartUserId);
      setUserResumes(resumes);
      setFullInterviewChartData(chartData);
      setInterviewData(chartData);
    } catch (error) {
      console.error('Failed to fetch user resumes:', error);
      toast.error('Failed to fetch user resumes');
    } finally {
      setChartLoading(false);
    }
  }
};

// ... existing code ...

  const handleCloseCallChart = () => {
    setCallChartOpen(false);
    setSelectedChartUser('');
    setSelectedChartResume('');
    setUserResumes([]);
    setInterviewData([]);
    setFullInterviewChartData([]);
    setCallChartCategoryFilter([]);
  };

  const handleChartUserChange = async (userId) => {
    setSelectedChartUser(userId);
    setSelectedChartResume('all');
    setUserResumes([]);
    setInterviewData([]);
    setFullInterviewChartData([]);
    
    if (userId) {
      try {
        setChartLoading(true);
        const { resumes, chartData } = await loadInterviewChartForUser(userId);
        setUserResumes(resumes);
        setFullInterviewChartData(chartData);
        setInterviewData(chartData);
      } catch (error) {
        console.error('Failed to fetch user resumes:', error);
        toast.error('Failed to fetch user resumes');
      } finally {
        setChartLoading(false);
      }
    }
  };

  const handleChartResumeChange = (resumeId) => {
    setSelectedChartResume(resumeId);

    if (resumeId === 'all') {
      setInterviewData(fullInterviewChartData);
      return;
    }

    if (resumeId) {
      setInterviewData(filterInterviewChartByResume(fullInterviewChartData, resumeId));
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Tabs */}
        <Box sx={{ width: '80%', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange} centered>
            <Tab label="Bids" />
            <Tab label="Mails" />
            <Tab label="Calls" />
          </Tabs>
        </Box>

        {/* Bid Tab Content */}
        {currentTab === 0 && (
          <>
            <Box sx={{ width: '80%', mb: 4, height: '200px' }}>
              <Paper sx={{ p: 2 }}>
                <Scatter 
                  data={getTimeScatterData(displayedBids)} 
                  options={timeScatterOptions}
                />
              </Paper>
            </Box>
            <Box sx={{ width: '80%', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ minWidth: 200 }}>
                {usersList.length > 0 ? (
                  <TextField
                    select
                    label={userRole === 'lead' || userRole === 'boss' ? "User" : "Select Applications"}
                    value={selectedUser}
                    onChange={(e) => handleUserChange(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    {usersList.map((entry) => (
                      <MenuItem key={entry._id} value={entry._id}>
                        {entry.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    My Applications
                  </Typography>
                )}
              </Box>
              <Typography variant="subtitle1">
                {globalSearchResults.length > 0 ? 'Search Results' : 'Total'}: {displayedBids.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={handleCopyAllLinks}
                  startIcon={<ContentCopyIcon />}
                >
                  Copy All Links
                </Button>
                <Button
                  variant="contained" 
                  color="primary" 
                  onClick={handleOpenDashboard}
                >
                  View Bid Chart
                </Button>
                <Button
                  variant="contained" 
                  color="secondary" 
                  onClick={handleOpenGridView}
                  startIcon={<GridViewIcon />}
                >
                  View All
                </Button>
              </Box>
            </Box>

        <TableContainer component={Paper} sx={{ maxWidth: '80%', padding: '20px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px">#</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
                    Link
                    <TextField
                      size="small"
                      placeholder="filter..."
                      value={titleFilter}
                      variant="standard"
                      onChange={(e) => setTitleFilter(e.target.value)}
                      sx={{ ml: 1 }}
                    />
                    <Tooltip title="Search My Bids + My Bidders (Ctrl+Enter)">
                      <Button
                        variant="outlined"
                        onClick={handleGlobalSearch}
                        disabled={isSearching}
                        sx={{ maxWidth: 'max-content' }}
                      >
                        {isSearching ? (
                          <CircularProgress size={20} />
                        ) : (
                          <ManageSearchIcon />
                        )}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Search All Team Members">
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleGlobalUserSearch}
                        disabled={isGlobalUserSearching}
                        sx={{ maxWidth: 'max-content' }}
                      >
                        {isGlobalUserSearching ? (
                          <CircularProgress size={20} />
                        ) : (
                          <PeopleIcon />
                        )}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Clear All Searches">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          handleClearSearch();
                          handleClearGlobalUserSearch();
                        }}
                        sx={{ maxWidth: 'max-content' }}
                      >
                        <CloseIcon />
                      </Button>
                    </Tooltip>
                  </Box>
                </TableCell>
                {shouldShowUserColumn() && (
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      User
                      <TextField
                        select
                        size="small"
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        variant="standard"
                        sx={{ minWidth: '120px' }}
                      >
                        <MenuItem value="">All Users</MenuItem>
                        {getUniqueUsers().map((user) => (
                          <MenuItem key={user} value={user}>
                            {user}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    Profile
                    <TextField
                      select
                      size="small"
                      value={profileFilter}
                      onChange={(e) => setProfileFilter(e.target.value)}
                      variant="standard"
                      sx={{ minWidth: '120px' }}
                    >
                      <MenuItem value="">All Profiles</MenuItem>
                      {getUniqueProfiles().map((profile) => (
                        <MenuItem key={profile} value={profile}>
                          {profile}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  Date
                  <TextField
                    type="date"
                    variant="standard"
                    size="small"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    sx={{ maxWidth: '120px' }}
                  />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedBids.map((bid, index) => (
                <TableRow key={bid._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Link 
                      href={getBidLinkHref(bid)}
                      target="_blank"
                      rel={EXTERNAL_LINK_REL}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isManualEmailBid(bid)) {
                          handleOpenDetails(bid);
                        } else {
                          handleOpenLinkDetails(bid);
                        }
                      }}
                      sx={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        "&:visited": (theme) => ({
                          color: theme.palette.mode === 'dark' ? '#e0b0ff' : 'purple'
                        })
                      }}
                    >
                      {truncateDisplayUrl(bid, 60)}
                    </Link>
                  </TableCell>
                  {shouldShowUserColumn() && (
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {bid.userName || getUserNameById(bid.userId) || 'Unknown User'}
                        </Typography>
                        {bid.userRole && bid.userRole !== 'bidder' && (
                          <Typography variant="caption" color="text.secondary">
                            {bid.userRole}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {bid.profile ? (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            backgroundColor: '#008B8B',
                            color: 'white', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            display: 'inline-block',
                            fontSize: '0.75rem'
                          }}
                        >
                          {bid.profile}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No profile
                        </Typography>
                      )}
                      {bid.usedCustomizedResume && (
                        <Chip
                          label="Customized"
                          size="small"
                          sx={{
                            backgroundColor: '#4a3f6b',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '22px'
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {TimezoneUtils.formatForDisplay(new Date(bid.timestamp), {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Link
                        component="button"
                        onClick={() => handleOpenDetails(bid)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Tooltip title="Details">
                          <ViewHeadlineIcon />
                        </Tooltip>
                      </Link>
                      {canDeleteBid(bid) && (
                        <Link
                          component="button"
                          onClick={() => handleDelete(bid._id)}
                          sx={{ cursor: 'pointer', color: 'error.main' }}
                        >
                          <Tooltip title="Delete">
                            <DeleteIcon />
                          </Tooltip>
                        </Link>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Loading indicator below the table */}
          {bidHistoryLoading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 4,
              borderTop: '1px solid #e0e0e0',
              mt: 2
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography sx={{ mt: 1, fontSize: '0.9rem', color: 'text.secondary' }}>
                  Loading bid history...
                </Typography>
              </Box>
            </Box>
          )}
        </TableContainer>
          </>
        )}

        {/* Mails Tab Content */}
        {currentTab === 1 && (
          <>
            <Box sx={{ width: '80%', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ minWidth: 200 }}>
                {callUsersList.length > 0 ? (
                  <TextField
                    select
                    label={userRole === 'lead' || userRole === 'boss' ? "User" : "Select Applications"}
                    value={selectedCallUser}
                    onChange={(e) => handleCallUserChange(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    {callUsersList.map((entry) => (
                      <MenuItem key={entry._id} value={entry._id}>
                        {entry.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    My Applications
                  </Typography>
                )}
              </Box>
              <Typography variant="subtitle1">
                {globalEmailSearchResults.length > 0 ? 'Search Results' : 'Total'}: {displayedEmails.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained" 
                  color="primary" 
                  onClick={handleOpenCallChart}
                  startIcon={<BarChartIcon />}
                >
                  View Call Chart
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ maxWidth: '80%', padding: '20px' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px">#</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
                        Title
                        <TextField
                          size="small"
                          placeholder="filter..."
                          value={emailTitleFilter}
                          variant="standard"
                          onChange={(e) => setEmailTitleFilter(e.target.value)}
                          sx={{ ml: 1 }}
                        />
                        <Tooltip title="Search Selected User Emails (Enter)">
                          <Button
                            variant="outlined"
                            onClick={handleEmailGlobalSearch}
                            disabled={isEmailSearching}
                            sx={{ maxWidth: 'max-content' }}
                          >
                            {isEmailSearching ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ManageSearchIcon />
                            )}
                          </Button>
                        </Tooltip>
                        <Tooltip title="Search All Team Members (Ctrl+Enter)">
                          <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleEmailGlobalUserSearch}
                            disabled={isGlobalUserEmailSearching}
                            sx={{ maxWidth: 'max-content' }}
                          >
                            {isGlobalUserEmailSearching ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PeopleIcon />
                            )}
                          </Button>
                        </Tooltip>
                        <Tooltip title="Clear All Searches">
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              handleClearEmailSearch();
                              handleClearGlobalUserEmailSearch();
                            }}
                            sx={{ maxWidth: 'max-content' }}
                          >
                            <CloseIcon />
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        From
                      </Box>
                    </TableCell>
                    {shouldShowEmailUserColumn() && (
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          User
                          <TextField
                            select
                            size="small"
                            value={emailUserFilter}
                            onChange={(e) => setEmailUserFilter(e.target.value)}
                            variant="standard"
                            sx={{ minWidth: '120px' }}
                          >
                            <MenuItem value="">All Users</MenuItem>
                            {getUniqueEmailUsers().map((user) => (
                              <MenuItem key={user} value={user}>
                                {user}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        Profile
                        <TextField
                          select
                          size="small"
                          value={emailProfileFilter}
                          onChange={(e) => setEmailProfileFilter(e.target.value)}
                          variant="standard"
                          sx={{ minWidth: '120px' }}
                        >
                          <MenuItem value="">All Profiles</MenuItem>
                          {getUniqueEmailProfiles().map((profile) => (
                            <MenuItem key={profile} value={profile}>
                              {profile}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        Category
                        <FormControl variant="standard" size="small" sx={{ minWidth: '120px' }}>
                          <Select
                            multiple
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : 'All'}
                          >
                            {emailCategories.map((category) => (
                              <MenuItem key={category} value={category}>
                                <Chip label={formatCategoryName(category)} size="small" />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      Date
                      <TextField
                        type="date"
                        variant="standard"
                        size="small"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        sx={{ maxWidth: '120px' }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedEmails.map((email, index) => (
                    <TableRow key={email._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Link 
                          component="button"
                          onClick={() => handleOpenEmailDetails(email)}
                          sx={{
                            textDecoration: 'none',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          {email.subject && email.subject.length > 60 ? email.subject.substring(0, 60) + '...' : email.subject || 'No Subject'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {email.from && email.from.length > 40 ? email.from.substring(0, 40) + '...' : email.from || 'Unknown'}
                        </Typography>
                      </TableCell>
                      {shouldShowEmailUserColumn() && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {email.userName || 'Unknown User'}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        {email.resumeName ? (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              backgroundColor: '#008B8B',
                              color: 'white', 
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1,
                              display: 'inline-block',
                              fontSize: '0.75rem'
                            }}
                          >
                            {email.resumeName}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No profile
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {email.category && (
                          <Chip 
                            label={formatCategoryName(email.category)} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {TimezoneUtils.formatForDisplay(new Date(email.receivedAt || email.processedAt), {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Loading indicator below the table */}
              {emailHistoryLoading && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  py: 4,
                  borderTop: '1px solid #e0e0e0',
                  mt: 2
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} />
                    <Typography sx={{ mt: 1, fontSize: '0.9rem', color: 'text.secondary' }}>
                      Loading email history...
                    </Typography>
                  </Box>
                </Box>
              )}
            </TableContainer>
          </>
        )}

        {/* Calls Tab Content */}
        {currentTab === 2 && <InterviewCallsTab />}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Application Details</Typography>
            <Box>
              <Tooltip title="View All Screenshots">
                <Button 
                  onClick={() => {
                    handleCloseDetails();
                    handleOpenGridView();
                  }}
                  sx={{ minWidth: '40px' }}
                >
                  <GridViewIcon />
                </Button>
              </Tooltip>
              <Tooltip title="Previous (Left Arrow)">
                <span>
                  <Button 
                    onClick={handlePreviousBid} 
                    disabled={selectedBidIndex <= 0}
                    sx={{ minWidth: '40px' }}
                  >
                    <NavigateBeforeIcon />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Next (Right Arrow)">
                <span>
                  <Button 
                    onClick={handleNextBid} 
                    disabled={selectedBidIndex >= displayedBids.length - 1}
                    sx={{ minWidth: '40px' }}
                  >
                    <NavigateNextIcon />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column', my: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {isManualEmailBid(selectedBid) ? 'Email' : 'URL'}
              </Typography>
              <Link 
                href={getBidLinkHref(selectedBid)}
                target="_blank"
                rel={EXTERNAL_LINK_REL}
                sx={{
                  textDecoration: 'none',
                  "&:visited": (theme) => ({
                    color: theme.palette.mode === 'dark' ? '#e0b0ff' : 'purple'
                  })
                }}
              >
                {getBidDisplayUrl(selectedBid)}
              </Link>
            </Box>
            {selectedBid?.timestamp && (
              <Box>
                <Typography variant="h6" gutterBottom>Date</Typography>
                <Typography variant="body1">
                  {TimezoneUtils.formatForDisplay(new Date(selectedBid.timestamp), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            )}
            {selectedBid?.screenshot && (
              <Box>
                <Typography variant="h6" gutterBottom>Screenshot</Typography>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', position: 'relative', minHeight: '200px' }}>
                  {imageLoading && (
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <CircularProgress />
                    </Box>
                  )}
                  <img 
                    key={selectedBid.screenshot}
                    src={`${process.env.REACT_APP_API_URL}/resumefiles/${selectedBid.screenshot}`}
                    alt="Application Screenshot" 
                    style={{ maxWidth: '100%', height: 'auto', display: imageLoading ? 'none' : 'block' }}
                    onLoad={() => setImageLoading(false)}
                    onError={(e) => {
                      // Prevent infinite error loop by setting onerror to null
                      e.target.onerror = null;
                      // Only try fallback if we haven't already tried it
                      if (!e.target.src.includes(process.env.REACT_APP_API_URL_PROD)) {
                        e.target.src = `${process.env.REACT_APP_API_URL_PROD}/resumefiles/${selectedBid.screenshot}`;
                      } else {
                        // If both sources fail, stop loading
                        setImageLoading(false);
                      }
                    }}
                  />
                </Box>
              </Box>
            )}
            
            {selectedBid?.filledFields && selectedBid.filledFields.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>Filled Fields</Typography>
                <List>
                  {selectedBid.filledFields.map((field, index) => (
                    field.trim() !== '' && (
                      <ListItem key={index}>
                        <ListItemText primary={field} />
                      </ListItem>
                    )
                  ))}
                </List>
              </Box>
            )}

            {selectedBid?.usedCustomizedResume && (
              <Box>
                <Typography variant="h6" gutterBottom>Customized Resume</Typography>
                {selectedBid.generatedResumePath ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleOpenCustomizedResume(selectedBid)}
                  >
                    View Customized Resume
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Customized resume was used, but the file is no longer available.
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Show sent email for email applications */}
            {selectedBid?.mailedResumeId && (
              <Box>
                <Typography variant="h6" gutterBottom>Sent Email</Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper', 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    To: {selectedBid.applicationEmail || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Subject: {selectedBid.mailedResumeId?.cover_letter?.title || 'Job Application'}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                    {selectedBid.mailedResumeId?.cover_letter?.content || 'Please find my resume attached.'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" sx={{ mr: 'auto', color: 'text.secondary' }}>
            {selectedBidIndex !== null ? `${selectedBidIndex + 1} of ${displayedBids.length}` : ''}
          </Typography>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={gridViewOpen}
        onClose={handleCloseGridView}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">All Applications</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ImageList cols={3} gap={16}>
            {displayedBids.filter(bid => bid.screenshot).map((bid) => (
              <ImageListItem 
                key={bid._id} 
                onClick={() => handleGridItemClick(bid)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              >
                <img
                  src={`${process.env.REACT_APP_API_URL}/resumefiles/${bid.screenshot}`}
                  alt="Application Screenshot"
                  loading="lazy"
                  style={{ height: '200px', objectFit: 'cover' }}
                  onError={(e) => {
                    // Prevent infinite error loop by setting onerror to null
                    e.target.onerror = null;
                    // Only try fallback if we haven't already tried it
                    if (!e.target.src.includes(process.env.REACT_APP_API_URL_PROD)) {
                      e.target.src = `${process.env.REACT_APP_API_URL_PROD}/resumefiles/${bid.screenshot}`;
                    }
                  }}
                />
                <ImageListItemBar
                  title={truncateDisplayUrl(bid, 40)}
                  subtitle={TimezoneUtils.formatForDisplay(new Date(bid.timestamp), {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                />
              </ImageListItem>
            ))}
          </ImageList>
          {displayedBids.filter(bid => bid.screenshot).length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography variant="h6">No screenshots available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGridView}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDashboard}
        onClose={handleCloseDashboard}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {dashboardProps ? (
            <Dashboard 
              userRole={dashboardProps.userRole}
              currentUserId={dashboardProps.currentUserId}
              teamMembers={dashboardProps.teamMembers}
              preloaded={dashboardProps.preloaded}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={callChartOpen}
        onClose={handleCloseCallChart}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Interview Invitation Call Chart</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* User Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Select User</Typography>
              <TextField
                select
                fullWidth
                value={selectedChartUser}
                onChange={(e) => handleChartUserChange(e.target.value)}
                variant="outlined"
                size="small"
              >
                {chartUsersList.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Resume Selection */}
            {selectedChartUser && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Select Resume</Typography>
                <TextField
                  select
                  fullWidth
                  value={selectedChartResume}
                  onChange={(e) => handleChartResumeChange(e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={chartLoading}
                >
                  <MenuItem value="all">
                    <strong>All Resumes</strong>
                  </MenuItem>
                  {userResumes.map((resume) => (
                    <MenuItem key={resume._id} value={resume._id}>
                      {formatResumeName(resume)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {/* Date Range Selection (similar to Bid Chart Dashboard) */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Date Range</Typography>
              <TextField
                select
                value={callChartDateRange}
                onChange={(e) => setCallChartDateRange(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ minWidth: 200 }}
                disabled={chartLoading}
              >
                <MenuItem value="7">Last 7 Days</MenuItem>
                <MenuItem value="14">Last 14 Days</MenuItem>
                <MenuItem value="30">Last 30 Days</MenuItem>
              </TextField>
            </Box>

            {/* Category Filter */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Filter by Category</Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  multiple
                  value={callChartCategoryFilter}
                  onChange={(e) => setCallChartCategoryFilter(e.target.value)}
                  renderValue={(selected) => 
                    selected.length === 0 
                      ? 'All Categories' 
                      : selected.map(cat => formatCategoryName(cat)).join(', ')
                  }
                  disabled={chartLoading}
                >
                  {emailCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      <Chip label={formatCategoryName(category)} size="small" />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {callChartCategoryFilter.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {callChartCategoryFilter.map((category) => (
                    <Chip
                      key={category}
                      label={formatCategoryName(category)}
                      size="small"
                      onDelete={() => {
                        setCallChartCategoryFilter(prev => prev.filter(c => c !== category));
                      }}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Chart Display */}
            {!chartLoading && (selectedChartResume === 'all' || selectedChartResume) && filteredInterviewData.length > 0 && (
              <Box sx={{ height: '400px', mt: 2 }}>
                <Line 
                  data={{
                    labels: filteredInterviewData.map(item => TimezoneUtils.formatDateWithDayOfWeek(item.date)),
                    datasets: [{
                      label: 'Unique Interview Invitations',
                      data: filteredInterviewData.map(item => item.count),
                      fill: false,
                      borderColor: 'rgb(75, 192, 192)',
                      backgroundColor: 'rgba(75, 192, 192, 0.2)',
                      tension: 0.1,
                      pointRadius: 6,
                      pointHoverRadius: 8
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      title: {
                        display: true,
                        text: selectedChartResume === 'all' 
                          ? 'Interview Invitations by Day - All Resumes (Combined)' 
                          : 'Interview Invitations by Day (Unique by From Address)'
                      },
                      legend: {
                        display: true
                      },
                      tooltip: {
                        enabled: false
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1,
                          callback: function(value) {
                            if (Number.isInteger(value)) {
                              return value;
                            }
                          }
                        },
                        title: {
                          display: true,
                          text: 'Number of Unique Invitations'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Date'
                        }
                      }
                    },
                    interaction: {
                      intersect: false,
                      mode: 'index'
                    },
                    onClick: (event, elements) => {
                      if (elements.length > 0) {
                        const dataIndex = elements[0].index;
                        const dateData = filteredInterviewData[dataIndex];
                        if (dateData && dateData.emails && dateData.emails.length > 0) {
                          setSelectedDateEmails(dateData.emails);
                          setChartDateDetailsOpen(true);
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}

            {(selectedChartResume === 'all' || selectedChartResume) && filteredInterviewData.length === 0 && !chartLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography variant="h6" color="text.secondary">
                  {selectedChartResume === 'all' 
                    ? 'No interview invitation data found for any resume'
                    : 'No interview invitation data found for this resume'}
                </Typography>
              </Box>
            )}

            {chartLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCallChart}>Close</Button>
        </DialogActions>
      </Dialog>

      <LinkDetailsDialog
        open={linkDetailsDialogOpen}
        onClose={() => setLinkDetailsDialogOpen(false)}
        link={selectedLink}
        loading={linkDetailsLoading}
        getUserNameById={getUserNameById}
        getRelativeTimeString={getRelativeTimeString}
        formatQueryDateLimit={formatQueryDateLimit}
        canEdit={user?.role !== 'bidder'}
        techStackOptions={(teamTechStacks || []).map((s) => ({ value: s.label, label: s.label }))}
        onLinkUpdated={(updatedLink) => setSelectedLink(updatedLink)}
      />

      {/* Email Details Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Email Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column', my: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>Subject</Typography>
                <Typography variant="body1">{selectedEmail.subject || 'No Subject'}</Typography>
              </Box>
              
              <Box>
                <Typography variant="h6" gutterBottom>From</Typography>
                <Typography variant="body1">{selectedEmail.from || 'Unknown'}</Typography>
              </Box>

              {selectedEmail.to && (
                <Box>
                  <Typography variant="h6" gutterBottom>To</Typography>
                  <Typography variant="body1">{selectedEmail.to}</Typography>
                </Box>
              )}

              {selectedEmail.category && (
                <Box>
                  <Typography variant="h6" gutterBottom>Category</Typography>
                  <Chip label={formatCategoryName(selectedEmail.category)} color="primary" />
                </Box>
              )}

              {selectedEmail.resumeName && (
                <Box>
                  <Typography variant="h6" gutterBottom>Resume/Profile</Typography>
                  {selectedEmail.resumePath || selectedEmail.resumeId ? (
                    <Link
                      component="button"
                      variant="body1"
                      onClick={() => handleOpenResumeFile(selectedEmail)}
                      sx={{
                        textAlign: 'left',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {selectedEmail.resumeName}
                    </Link>
                  ) : (
                    <Typography variant="body1">{selectedEmail.resumeName}</Typography>
                  )}
                </Box>
              )}

              {selectedEmail.userName && (
                <Box>
                  <Typography variant="h6" gutterBottom>User</Typography>
                  <Typography variant="body1">{selectedEmail.userName}</Typography>
                </Box>
              )}

              {(selectedEmail.receivedAt || selectedEmail.processedAt) && (
                <Box>
                  <Typography variant="h6" gutterBottom>Date</Typography>
                  <Typography variant="body1">
                    {TimezoneUtils.formatForDisplay(new Date(selectedEmail.receivedAt || selectedEmail.processedAt), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              )}

              {emailBodyLoading && !selectedEmail.body && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {selectedEmail.body && (
                <Box>
                  <Typography variant="h6" gutterBottom>Full Content</Typography>
                  <Box sx={(theme) => ({
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'background.paper',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                    '& div, & p, & span, & td, & th, & li, & font, & h1, & h2, & h3, & h4, & h5, & h6': theme.palette.mode === 'dark'
                      ? { color: '#ffffff !important' }
                      : {},
                    '& a': theme.palette.mode === 'dark'
                      ? { color: '#00d9ff !important' }
                      : {},
                  })}>
                    {selectedEmail.body.html ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedEmail.body.html }} />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                        })}
                      >
                        {selectedEmail.body.text || 'No content'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Chart Date Details Dialog - Shows emails grouped by resume */}
      <Dialog
        open={chartDateDetailsOpen}
        onClose={() => setChartDateDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Interview Invitations</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            {(() => {
              // Group emails by resume
              const emailsByResume = {};
              selectedDateEmails.forEach(email => {
                const resumeKey = email.resumeName || email.resumeId || 'Unknown Resume';
                if (!emailsByResume[resumeKey]) {
                  emailsByResume[resumeKey] = [];
                }
                emailsByResume[resumeKey].push(email);
              });

              return Object.entries(emailsByResume).map(([resumeName, emails], resumeIndex) => (
                <Box key={resumeIndex}>
                  {/* Resume Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    mb: 2,
                    mt: resumeIndex > 0 ? 3 : 0
                  }}>
                    <Chip 
                      label={resumeName}
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      ({emails.length} {emails.length === 1 ? 'email' : 'emails'})
                    </Typography>
                  </Box>

                  {/* Emails for this resume */}
                  {emails.map((email, emailIndex) => (
                    <Box 
                      key={emailIndex}
                      sx={{ 
                        mb: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Subject */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {email.subject || 'No Subject'}
                        </Typography>

                        {/* From */}
                        <Typography variant="body2" color="text.secondary">
                          <strong>From:</strong> {email.from || 'Unknown'}
                        </Typography>

                        {/* Category */}
                        {email.category && (
                          <Box>
                            <Chip 
                              label={formatCategoryName(email.category)} 
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        )}

                        {/* Time */}
                        {(email.receivedAt || email.processedAt) && (
                          <Typography variant="caption" color="text.secondary">
                            {TimezoneUtils.formatForDisplay(new Date(email.receivedAt || email.processedAt), {
                              year: 'numeric',
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        )}

                        {/* Snippet */}
                        {email.snippet && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              mt: 1,
                              fontStyle: 'italic',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {email.snippet}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {/* Divider between resumes */}
                  {resumeIndex < Object.entries(emailsByResume).length - 1 && (
                    <Divider sx={{ my: 3 }} />
                  )}
                </Box>
              ));
            })()}

            {selectedDateEmails.length === 0 && (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No emails to display
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChartDateDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BidHistory; 